import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ContractWizardState, ContractEvent } from '../../types/contract';
import { useNotifications } from '../../contexts/NotificationContext';
import { createContract, getContract, updateContract } from '../../services/contractApiService';
import ContractDetailsStep from './steps/ContractDetailsStep';
import DataNormalizationTab from './DataNormalizationTab';
import SummaryStep from './steps/SummaryStep';
import LabsAppBar, { ActionButton, BreadcrumbItem } from '../LabsAppBar';
import { useMediaQuery } from '../../hooks/useMediaQuery'; // Import useMediaQuery hook

const STEPS = [
  { id: 1, title: 'Contract Details' },
  { id: 2, title: 'Upload & Normalize Events' }, // Combined step
  { id: 3, title: 'Summary & Review' } // Renumbered
];

const ContractWizard: React.FC = () => {
  const { contractId } = useParams<{ contractId: string }>();
  const navigate = useNavigate();
  const { showNotification } = useNotifications();
  
  const [state, setState] = useState<ContractWizardState>({
    contractId: null,
    name: '',
    description: '',
    selectedSchemaIds: [],
    selectedIntegrationIds: [],
    events: [],
    allContractProperties: [],
    normalizedProperties: {},
    isNewContract: contractId === 'new',
    mappingProgress: {
      total: 0,
      mapped: 0,
      required: 0,
      requiredMapped: 0
    },
    currentStep: 1,
    isSubmitting: false,
    status: 'draft' // Default status for new contracts
  });
  
  // State for selected event in DataNormalizationTab
  const [selectedEvent, setSelectedEvent] = useState<ContractEvent | null>(null);
  
  const [isLoading, setIsLoading] = useState(contractId !== 'new');
  const [isFinalizing, setIsFinalizing] = useState(false);
  const isSmallScreen = useMediaQuery('(max-width: 1600px)'); // Define breakpoint for small screens
  
  // Load existing contract if editing
  useEffect(() => {
    console.log('ContractWizard useEffect: contractId =', contractId);
    const loadContract = async () => {
      if (contractId && contractId !== 'new') {
        try {
          console.log('ContractWizard: Setting isLoading to true, attempting to load contract...');
          setIsLoading(true);
          const contract = await getContract(contractId);
          console.log('ContractWizard: Contract loaded successfully:', contract);
          
          // Transform contract data into wizard state
          setState({
            name: contract.name,
            description: contract.data.description || '',
            selectedSchemaIds: contract.data.schemaIds || [],
            selectedIntegrationIds: contract.data.integrationIds || [],
            events: contract.data.events || [],
            normalizedProperties: contract.data.normalizedProperties || {},
            mappingProgress: contract.data.mappingProgress || {
              total: 0,
              mapped: 0,
              required: 0,
              requiredMapped: 0
            },
            contractId: contract.id,
            shareToken: contract.share_token, // Load share_token into state
            status: contract.status || 'draft', // Load status into state, default to 'draft'
            currentStep: 1,
            isSubmitting: false,
            allContractProperties: contract.data.allContractProperties || [],
            isNewContract: false,
            customerName: contract.data.customerName,
            customerEmail: contract.data.customerEmail
          });
          
          showNotification('success', 'Contract loaded successfully');
        } catch (error) {
          console.error('ContractWizard: Failed to load contract:', error);
          showNotification('error', 'Failed to load contract');
          navigate('/labs/contracts');
        } finally {
          console.log('ContractWizard: Setting isLoading to false.');
          setIsLoading(false);
        }
      } else {
        console.log('ContractWizard: contractId is "new", not loading existing contract.');
        setIsLoading(false); // Ensure isLoading is false for new contracts
      }
    };
    
    loadContract();
  }, [contractId, navigate, showNotification]);
  
  // Handle finalize contract (moved from SummaryStep)
  const handleFinalizeContract = useCallback(async () => {
    if (!state.contractId) {
      showNotification('error', 'Please save the contract as a draft first');
      return;
    }
    
    try {
      setIsFinalizing(true);
      
      // Update contract status to pending
      await updateContract(state.contractId, {
        status: 'pending'
      });
      
      showNotification('success', 'Contract finalized successfully');
      navigate('/labs/contracts'); // Navigate back to the contracts list
    } catch (error) {
      console.error('Failed to finalize contract:', error);
      showNotification('error', 'Failed to finalize contract');
    } finally {
      setIsFinalizing(false);
    }
  }, [state.contractId, navigate, showNotification]);
  
  // Save contract as draft
  const saveAsDraft = useCallback(async () => {
    try {
      setState(prev => ({ ...prev, isSubmitting: true }));
      
      const contractData = {
        description: state.description,
        schemaIds: state.selectedSchemaIds,
        integrationIds: state.selectedIntegrationIds,
        events: state.events,
        normalizedProperties: state.normalizedProperties,
        mappingProgress: state.mappingProgress,
        customerName: state.customerName, // Include customer details if available
        customerEmail: state.customerEmail, // Include customer details if available
        allContractProperties: state.allContractProperties
      };

      let savedContract;

      if (state.contractId) {
        // Update existing contract
        savedContract = await updateContract(state.contractId, {
          name: state.name,
          status: 'draft',
          data: contractData,
          share_token: state.shareToken, // Include share_token in update
        });
      } else {
        // Create new contract
        savedContract = await createContract({
          name: state.name,
          schema_id: state.selectedSchemaIds[0] || '', // Use first schema as primary
          data: contractData,
          share_token: state.shareToken, // Include share_token in creation
        });
      }
      
      setState(prev => ({ 
        ...prev, 
        contractId: savedContract.id,
        isSubmitting: false 
      }));
      
      showNotification('success', 'Contract saved as draft');
      return savedContract;
    } catch (error) {
      console.error('Failed to save contract:', error);
      showNotification('error', 'Failed to save contract');
      setState(prev => ({ ...prev, isSubmitting: false }));
      throw error;
    }
  }, [state, showNotification]);
  
  // Auto-save on step change
  const handleStepChange = useCallback(async (newStep: number) => {
    try {
      // Save current progress before changing step
      if (state.name) {
        await saveAsDraft();
      }
      
      setState(prev => ({ ...prev, currentStep: newStep }));
    } catch (error) {
      console.error('Failed to save progress:', error);
    }
  }, [state.name, saveAsDraft]);
  
  const goToNextStep = useCallback(() => {
    if (state.currentStep < STEPS.length) {
      handleStepChange(state.currentStep + 1);
    }
  }, [state.currentStep, handleStepChange]);
  
  const updateState = useCallback((updates: Partial<ContractWizardState>) => {
    setState(prev => ({ ...prev, ...updates }));
  }, []);
  
  // Define breadcrumbs for LabsAppBar
  const breadcrumbs: BreadcrumbItem[] = [
    { name: 'Data Contracts', iconName: 'SquareCheckBig', path: '/labs/contracts' },
    { name: contractId === 'new' ? 'New Contract' : state.name },
  ];

  // Define actions for LabsAppBar
  const actions: ActionButton[] = [
    {
      label: 'Save as Draft',
      onClick: saveAsDraft,
      iconName: 'Save',
      variant: 'secondary',
      disabled: state.isSubmitting || !state.name || isFinalizing, // Disable if finalizing
    },
    state.currentStep < STEPS.length ?
    {
      label: 'Next',
      onClick: goToNextStep,
      iconName: 'ArrowRight',
      variant: 'primary',
      disabled: state.isSubmitting || isFinalizing, // Disable if finalizing
    } :
    {
      label: isFinalizing ? 'Finalizing...' : 'Finalize Contract', // Show loading text
      onClick: handleFinalizeContract, // Call the new handleFinalizeContract
      iconName: 'SquareCheckBig',
      variant: 'primary',
      disabled: state.isSubmitting || isFinalizing, // Disable if submitting or finalizing
    }
  ];

  // Render loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  return (
    <div className="flex flex-col min-h-screen bg-gray-50 dark:bg-gray-900">
      <LabsAppBar
        breadcrumbs={breadcrumbs}
        actions={actions}
        variant="stepper"
        steps={STEPS}
        currentStep={state.currentStep}
        onStepClick={handleStepChange}
        isSmallScreen={isSmallScreen} // Pass the new prop
      />
      <div className="flex-1 relative overflow-y-auto overflow-x-auto p-4 sm:p-6 lg:p-8 min-w-[800px]">
        <div className="flex flex-col gap-10 max-w-9xl mx-auto pb-24">
          {state.currentStep === 1 && (
            <ContractDetailsStep 
              state={state} 
              updateState={updateState} 
            />
          )}
          
          {state.currentStep === 2 && (
            <DataNormalizationTab 
              state={state} 
              updateState={updateState}
              selectedEvent={selectedEvent}
              onEventSelect={setSelectedEvent}
              handleAddProperty={(eventId, parentProperty) => {
                // Implementation for adding a property
                const newProperty = {
                  id: crypto.randomUUID(),
                  name: '',
                  path: parentProperty ? `${parentProperty.path}.newProperty` : 'newProperty',
                  codeName: 'newProperty',
                  isNormalized: false,
                  eventId: eventId,
                  // ContractPropertyTreeNode specific properties
                  children: [],
                  hasChildren: false,
                  isExpanded: false,
                  isLastChild: false,
                  level: parentProperty && typeof parentProperty.level === 'number' ? parentProperty.level + 1 : 0
                };
                
                const updatedProperties = [...state.allContractProperties, newProperty];
                updateState({ allContractProperties: updatedProperties });
                return newProperty.id;
              }}
              handleUpdateProperty={(propertyId, updates) => {
                const updatedProperties = state.allContractProperties.map(prop => 
                  prop.id === propertyId ? { ...prop, ...updates } : prop
                );
                
                const updatedNormalizedProperties = { ...state.normalizedProperties };
                if (updates.isNormalized) {
                  const property = updatedProperties.find(p => p.id === propertyId);
                  if (property) {
                    updatedNormalizedProperties[propertyId] = {
                      ...property,
                      ...updates
                    };
                  }
                } else if (updates.isNormalized === false) {
                  delete updatedNormalizedProperties[propertyId];
                }
                
                updateState({ 
                  allContractProperties: updatedProperties,
                  normalizedProperties: updatedNormalizedProperties
                });
              }}
              handleDeleteProperty={(propertyId, deleteFromAllEvents) => {
                const propertyToDelete = state.allContractProperties.find(p => p.id === propertyId);
                if (!propertyToDelete) return;
                
                let updatedProperties;
                if (deleteFromAllEvents) {
                  // Delete from all events
                  updatedProperties = state.allContractProperties.filter(p => p.id !== propertyId);
                } else {
                  // Delete only from current event
                  updatedProperties = state.allContractProperties.filter(p => 
                    p.id !== propertyId || p.eventId !== propertyToDelete.eventId
                  );
                }
                
                const updatedNormalizedProperties = { ...state.normalizedProperties };
                delete updatedNormalizedProperties[propertyId];
                
                updateState({
                  allContractProperties: updatedProperties,
                  normalizedProperties: updatedNormalizedProperties
                });
              }}
            />
          )}
          
          {state.currentStep === 3 && (
            <SummaryStep 
              state={state} 
              updateState={updateState} 
              saveAsDraft={saveAsDraft}
              isFinalizing={isFinalizing} // Pass isFinalizing prop
            />
          )}
        </div>
      </div>

      {/* Removed duplicate navigation bar - LabsAppBar already provides navigation controls */}
    </div>
  );
};

export default ContractWizard;
